'use client';

import React from 'react';
import { Play } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PostMedia } from './post-media';
import type { PostMediaItem } from '@/lib/posts/media';

/**
 * Renders a post's media. ONE item falls through to the rich {@link PostMedia}
 * (full video/audio/3D players, portrait-aware). TWO+ items render an X-style
 * gallery grid (2 = side-by-side, 3 = one tall + two stacked, 4+ = 2x2 with a
 * "+N" overlay on the last tile). Clicking any tile opens the lightbox at that
 * index, where the user can swipe the full carousel.
 */

function GalleryTile({
  item,
  onClick,
  className,
  more,
}: {
  item: PostMediaItem;
  onClick?: (e: React.MouseEvent) => void;
  className?: string;
  /** When set (>0), render a "+N" overlay instead of the play badge. */
  more?: number;
}) {
  const isVideo = item.type === 'video';
  const poster = item.thumbnailUrl || undefined;
  const showMore = more != null && more > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn('group relative block w-full overflow-hidden bg-muted', className)}
    >
      {isVideo && !poster ? (
        // First frame as the still; the lightbox plays it.
        <video src={item.url} muted playsInline preload="metadata" className="h-full w-full object-cover" />
      ) : (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={poster || item.url}
          alt=""
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
        />
      )}
      {isVideo && !showMore && (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-black/55 backdrop-blur-sm">
            <Play className="ml-0.5 h-5 w-5 fill-white text-white" />
          </span>
        </span>
      )}
      {showMore && (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/55 text-2xl font-semibold text-white">
          +{more}
        </span>
      )}
    </button>
  );
}

interface PostMediaGalleryProps {
  media: PostMediaItem[];
  compact?: boolean;
  className?: string;
  /** Open the lightbox at the clicked item index. */
  onItemClick?: (index: number, e: React.MouseEvent) => void;
  // Single-item passthroughs to PostMedia:
  title?: string;
  priority?: boolean;
  preloaded?: boolean;
}

export function PostMediaGallery({
  media,
  compact,
  className,
  onItemClick,
  title,
  priority,
  preloaded,
}: PostMediaGalleryProps) {
  if (!media || media.length === 0) return null;

  // Single item → the existing rich renderer (full player, portrait handling).
  if (media.length === 1) {
    const m = media[0];
    return (
      <PostMedia
        mediaUrl={m.url}
        mediaType={m.type}
        thumbnailUrl={m.thumbnailUrl}
        mediaWidth={m.width ?? undefined}
        mediaHeight={m.height ?? undefined}
        title={title}
        compact={compact}
        className={className}
        onMediaClick={onItemClick ? (e) => onItemClick(0, e) : undefined}
        priority={priority}
        preloaded={preloaded}
      />
    );
  }

  const tiles = media.slice(0, 4);
  const extra = media.length - 4;
  const heightClass = compact ? 'h-72 sm:h-80' : 'h-96';
  const gridBase = cn('grid gap-0.5 overflow-hidden rounded-xl', heightClass, className);
  const click = (i: number) => (onItemClick ? (e: React.MouseEvent) => onItemClick(i, e) : undefined);

  if (tiles.length === 2) {
    return (
      <div className={cn(gridBase, 'grid-cols-2')}>
        <GalleryTile item={tiles[0]} onClick={click(0)} className="h-full" />
        <GalleryTile item={tiles[1]} onClick={click(1)} className="h-full" />
      </div>
    );
  }

  if (tiles.length === 3) {
    return (
      <div className={cn(gridBase, 'grid-cols-2 grid-rows-2')}>
        <GalleryTile item={tiles[0]} onClick={click(0)} className="row-span-2 h-full" />
        <GalleryTile item={tiles[1]} onClick={click(1)} className="h-full" />
        <GalleryTile item={tiles[2]} onClick={click(2)} className="h-full" />
      </div>
    );
  }

  // 4+ → 2x2, with "+N" on the last tile when there are more.
  return (
    <div className={cn(gridBase, 'grid-cols-2 grid-rows-2')}>
      {tiles.map((m, i) => (
        <GalleryTile
          key={i}
          item={m}
          onClick={click(i)}
          className="h-full"
          more={i === 3 && extra > 0 ? extra : undefined}
        />
      ))}
    </div>
  );
}
